import React, { useCallback, useContext, useState } from 'react'
import { UserContext } from '../UserContext'
import {
    TIMESTAMP_DISPLAY_LABELS,
    useTimestampDisplay,
} from '../../hooks/useTimestampDisplay'
import {
    formatCheckinTime,
    TIMESTAMP_DISPLAY_MODES,
    type TimestampDisplay,
} from '../../utils/dates'

/**
 * Whether the timeline shows a checkin's time of day, and as what.
 *
 * Stored server-side (`users.timestamp_display`) rather than in localStorage,
 * so it follows the user between their laptop, their phone and the dev clones'
 * separate origins. It arrives on the user object, so the only call here is the
 * write; the context refresh is what re-renders the timeline.
 *
 * The three options are shown with the same sample time rendered each way,
 * because "approximate" and "precise" mean nothing as words — seeing `2:34pm`
 * next to `afternoon` is the whole explanation.
 *
 * This is one of three places the setting can be changed; the others are the
 * check-ins page's sort menu and a tap on any date label. All three go through
 * `useTimestampDisplay`, and all three mark the setting *chosen* — which is
 * what retires the one-shot auto-set, including when the choice is "Not at
 * all".
 */

const SAMPLE_TIME = '14:34:00'

const TimestampDisplaySettings: React.FC = () => {
    const { user } = useContext(UserContext)
    const { mode: current, choose } = useTimestampDisplay()
    const [busy, setBusy] = useState(false)

    const handlePick = useCallback(
        async (mode: TimestampDisplay) => {
            if (!user || mode === current) return
            setBusy(true)
            try {
                await choose(mode)
            } finally {
                setBusy(false)
            }
        },
        [choose, current, user],
    )

    if (!user) return null

    return (
        <div className="mt-8 pt-6 border-t border-gray-200">
            <h2 className="text-lg font-semibold mb-1">Times on the timeline</h2>
            <p className="text-sm text-gray-600 mb-4">
                Whether a check-in shows the time of day it happened.
            </p>

            <div className="flex flex-col gap-2">
                {TIMESTAMP_DISPLAY_MODES.map(mode => {
                    const sample = formatCheckinTime(SAMPLE_TIME, mode)
                    return (
                        <label
                            key={mode}
                            className="flex items-center gap-3 cursor-pointer"
                        >
                            <input
                                type="radio"
                                name="timestamp-display"
                                className="h-4 w-4"
                                checked={current === mode}
                                disabled={busy}
                                onChange={() => { void handlePick(mode) }}
                            />
                            <span className="text-sm">{TIMESTAMP_DISPLAY_LABELS[mode]}</span>
                            {sample && (
                                <span className="text-sm text-gray-500">
                                    Wed, Sep 23 <span className="text-gray-700">{sample}</span>
                                </span>
                            )}
                        </label>
                    )
                })}
            </div>
        </div>
    )
}

export default TimestampDisplaySettings
